import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SelectEntity from "components/SelectEntity";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  AvailableMenuListRequest,
  ListRequestOfBaseFilter,
  ListResponseOfAdminMenuType,
  ListResponseOfMenu,
  ListResponseOfOrganizationMenu,
  OrganizationDetails,
  OrganizationMenu,
  OrganizationMenuRequest,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import OrganizationDynamicMenuActions from "./OrganizationDynamicMenuActions";

type OrganizationDynamicMenusProps = {
  organization: OrganizationDetails;
};

const ALL_RECORDS_PAGE_SIZE = -1;

const useStyles = (theme: Theme) => ({
  header: {
    display: "flex",
    justifyContent: "end",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
  feedback: {
    width: "100%",
    margin: "20px 0 0 15px",
  },
});

const OrganizationDynamicMenus = ({
  organization,
}: OrganizationDynamicMenusProps) => {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    organizationMenuService,
    menuService,
    menuDetailsUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("menu.menuType.name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [assignOpen, assignOpenActions] = useBoolean(false);
  const [unassignConfirmOpen, unassignConfirmOpenActions] = useBoolean(false);
  const [assignmentIdToRemove, setAssignmentIdToRemove] = useState<string>();
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState<string>();
  const [selectedMenuId, setSelectedMenuId] = useState<string>();

  const organizationMenus = useStrictQuery<ListResponseOfOrganizationMenu>(
    ["organizationMenuService.getOrganizationMenus", organization.id, page, rowsPerPage, orderBy, order],
    () =>
      organizationMenuService.getOrganizationMenus(
        organization.id,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const fetchedMenuTypes = useQuery<ListResponseOfAdminMenuType>(
    ["menuService.getMenuTypes"],
    () =>
      menuService.getMenuTypes(
        ListRequestOfBaseFilter.fromJS({ sortField: "name" })
      )
  );

  const fetchedMenus = useQuery<ListResponseOfMenu>(
    ["menuService.getAvailableMenus", organization.id, selectedMenuTypeId],
    () =>
      menuService.getAvailableMenus(
        AvailableMenuListRequest.fromJS({
          sortField: "name",
          pageSize: ALL_RECORDS_PAGE_SIZE,
          organizationId: organization.id,
          parameters: { menuTypeId: selectedMenuTypeId },
        })
      ),
    { enabled: !!selectedMenuTypeId }
  );

  useEffect(() => {
    setPage(0);
  }, [organization.id]);

  const isInherited = (menu: OrganizationMenu) =>
    menu.organizationId !== organization.id;

  const rows = (organizationMenus.data?.data ?? []).map(
    (m) =>
      ({
        ...m,
        color: isInherited(m) ? theme.palette.grey[200] : undefined,
        tooltip: isInherited(m)
          ? t(
              { id: "dynamicMenuIsInherited" },
              { organizationName: m.organizationName }
            )
          : undefined,
      } as OrganizationMenu & LazyTableRow)
  );

  const MenuActions = ({ value }: { value: OrganizationMenu }) => (
    <OrganizationDynamicMenuActions
      onView={() => navigate(menuDetailsUrl(value.menuId))}
      onUnassign={
        isInherited(value)
          ? undefined
          : () => {
              setAssignmentIdToRemove(value.id);
              unassignConfirmOpenActions.setTrue();
            }
      }
    />
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "menuType",
      customSortAccessor: "menu.menuType.name",
      type: "text",
      label: t({ id: "menuTypeLabel" }),
    },
    {
      accessor: "menuName",
      customSortAccessor: "menu.name",
      type: "text",
      label: t({ id: "menuName" }),
    },
    {
      accessor: "menuDescription",
      customSortAccessor: "menu.description",
      type: "text",
      label: t({ id: "descriptionLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      type: "text",
      label: t({ id: "inheritedFrom" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: MenuActions,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const closeAssignDialog = () => {
    assignOpenActions.setFalse();
    setSelectedMenuTypeId(undefined);
    setSelectedMenuId(undefined);
    setSubmitError(null);
  };

  const assignMenu = async () => {
    try {
      setSubmitError(null);

      const result = await organizationMenuService.assignMenu(
        OrganizationMenuRequest.fromJS({
          organizationId: organization.id,
          menuId: selectedMenuId,
        })
      );

      if (result.responseCode === ResponseCode.OK) {
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "dynamicMenuAssignSuccess" }))
        );
        queryClient.invalidateQueries(["organizationMenuService.getOrganizationMenus", organization.id]);
        queryClient.invalidateQueries(["menuService.getMenuUsage", selectedMenuId]);
        closeAssignDialog();
        return;
      }

      setSubmitError(
        result.responseReason ?? t({ id: "menuAlreadyAssignedForType" })
      );
    } catch (error) {
      setSubmitError(translateError(error, t));
    }
  };

  const unassignMenu = async () => {
    try {
      const result = await organizationMenuService.unassignMenu(
        assignmentIdToRemove!
      );

      if (result.responseCode === ResponseCode.OK) {
        queryClient.invalidateQueries(["organizationMenuService.getOrganizationMenus", organization.id]);
        queryClient.invalidateQueries("menuService.getMenuUsage");
        setPage(0);
      } else {
        setSnackbarMessage(
          new SnackbarMessageProps(result.responseReason ?? "", Severity.Error)
        );
      }
    } catch (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error)
      );
    } finally {
      unassignConfirmOpenActions.setFalse();
      setAssignmentIdToRemove(undefined);
    }
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: organizationMenus.data?.totalRecords ?? 0,
          rows,
          loading: organizationMenus.isFetching,
          titleComponent: (
            <Box sx={styles.header}>
              <Button onClick={assignOpenActions.setTrue} startIcon={<AddIcon />}>
                <T id="assignDynamicMenu" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <Dialog
        open={assignOpen}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        onClose={closeAssignDialog}
      >
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SelectEntity
                  fetchedData={fetchedMenuTypes}
                  labelField="name"
                  label={t({ id: "menuTypeLabel" })}
                  value={selectedMenuTypeId}
                  onChange={(v?: string) => {
                    setSelectedMenuTypeId(v);
                    setSelectedMenuId(undefined);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <SelectEntity
                  fetchedData={fetchedMenus}
                  labelField="name"
                  label={t({ id: "menu" })}
                  value={selectedMenuId}
                  disabled={!selectedMenuTypeId}
                  onChange={(v?: string) => setSelectedMenuId(v)}
                />
              </FormControl>
            </Grid>
            {submitError && (
              <Alert severity="error" sx={styles.feedback}>
                {submitError}
              </Alert>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog}>
            <T id="cancelButton" />
          </Button>
          <Button onClick={assignMenu} disabled={!selectedMenuId}>
            <T id="assign" />
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={unassignConfirmOpen}
        dialogTitleKey="unassignDynamicMenuTitle"
        contentKey="unassignDynamicMenuInfo"
        handleConfirm={unassignMenu}
        onCancel={() => {
          unassignConfirmOpenActions.setFalse();
          setAssignmentIdToRemove(undefined);
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationDynamicMenus);
